import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Dropdown, DropdownButton, Form, Offcanvas } from 'react-bootstrap';
import { canInsertOutlineLevel, GUIDE_TITLE_PLACEHOLDER, GUIDE_TITLE_PLACEHOLDER_ID } from './guidesUtils';
import useActiveOutlineHeading from './useActiveOutlineHeading';

function outlineLevelOf(heading) {
  return heading?.outlineLevel || 1;
}

function outlineParentId(headings, index) {
  const level = outlineLevelOf(headings[index]);
  for (let i = index - 1; i >= 0; i -= 1) {
    if (outlineLevelOf(headings[i]) < level) return headings[i].id;
  }
  return null;
}

function sectionLastIndex(headings, index) {
  const level = outlineLevelOf(headings[index]);
  let last = index;
  for (let i = index + 1; i < headings.length; i += 1) {
    if (outlineLevelOf(headings[i]) <= level) break;
    last = i;
  }
  return last;
}

function headingIsInside(headings, ancestorIndex, index) {
  return index > ancestorIndex && index <= sectionLastIndex(headings, ancestorIndex);
}

function hasOutlineSibling(headings, index) {
  const level = outlineLevelOf(headings[index]);
  const parent = outlineParentId(headings, index);
  return headings.some((heading, i) => (
    i !== index
    && outlineLevelOf(heading) === level
    && outlineParentId(headings, i) === parent
  ));
}

function cssEscape(value) {
  if (typeof CSS !== 'undefined' && CSS.escape) return CSS.escape(value);
  return String(value).replace(/"/g, '\\"');
}

function outlineRoleClass(level) {
  if (level <= 1) return 'guides-outline-section';
  if (level === 2) return 'guides-outline-topic';
  return 'guides-outline-subtopic';
}

/** Section and topic rows that contain the heading currently in view. */
function outlineAncestorIds(headings, activeId) {
  const ids = new Set();
  const index = headings.findIndex((heading) => heading.id === activeId);
  if (index < 0) return ids;
  let level = outlineLevelOf(headings[index]);
  for (let i = index - 1; i >= 0 && level > 1; i -= 1) {
    const next = outlineLevelOf(headings[i]);
    if (next < level) {
      ids.add(headings[i].id);
      level = next;
    }
  }
  return ids;
}

/**
 * Sibling slot for a drag. Topics reorder with other topics, and a subtopic
 * reorders with subtopics under the same parent. Nested rows count as the
 * parent section they belong to.
 */
function resolveOutlineDrop(headings, sourceId, hoverId) {
  const sourceIndex = headings.findIndex((h) => h.id === sourceId);
  const hoverIndex = headings.findIndex((h) => h.id === hoverId);
  if (sourceIndex < 0 || hoverIndex < 0) return null;
  if (sourceIndex === hoverIndex || headingIsInside(headings, sourceIndex, hoverIndex)) return null;

  const sourceLevel = outlineLevelOf(headings[sourceIndex]);
  const sourceParent = outlineParentId(headings, sourceIndex);
  let targetIndex = hoverIndex;

  if (outlineLevelOf(headings[hoverIndex]) !== sourceLevel) {
    targetIndex = -1;
    if (outlineLevelOf(headings[hoverIndex]) > sourceLevel) {
      for (let i = hoverIndex; i >= 0; i -= 1) {
        const level = outlineLevelOf(headings[i]);
        if (level < sourceLevel) break;
        if (level === sourceLevel) {
          targetIndex = i;
          break;
        }
      }
    }
  }

  if (targetIndex < 0 || targetIndex === sourceIndex) return null;
  if (headingIsInside(headings, sourceIndex, targetIndex)) return null;
  if (outlineLevelOf(headings[targetIndex]) !== sourceLevel) return null;
  if (outlineParentId(headings, targetIndex) !== sourceParent) return null;

  const place = sourceIndex < targetIndex ? 'after' : 'before';
  const markerIndex = place === 'after' ? sectionLastIndex(headings, targetIndex) : targetIndex;
  return {
    targetId: headings[targetIndex].id,
    place,
    markerId: headings[markerIndex].id
  };
}

/**
 * Sidebar outline for topic/article editors.
 * Document title is listed separately; body headings use a dynamic
 * topic / subtopic / third-level hierarchy relative to the selected topic heading.
 */
export default function GuideOutlineSidebar({
  headings = [],
  titleFallback = '',
  titleEmpty = false,
  topicHeadingLevel = 1,
  onScrollTo,
  onAddTopic,
  onAddSubtopic,
  onAddLowerSubtopic,
  onRemoveSection,
  onMoveSection,
  mobileToggleHost = null,
  readOnly = false,
  children = null
}) {
  const [adding, setAdding] = useState(null); // { outlineLevel, afterHeadingId } | null
  const [draftName, setDraftName] = useState('');
  const [outlineOpen, setOutlineOpen] = useState(false);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const dragRef = useRef(null);
  const activeHeadingId = useActiveOutlineHeading(
    headings.map((h) => h.id),
    '.guides-topic-editor-col .rte-editable-root'
  );

  const clearDropMarkers = () => {
    listRef.current?.querySelectorAll('.is-drop-before, .is-drop-after').forEach((el) => {
      el.classList.remove('is-drop-before', 'is-drop-after');
    });
  };

  const clearDragClasses = () => {
    listRef.current?.querySelectorAll('.is-dragging').forEach((el) => {
      el.classList.remove('is-dragging');
    });
    clearDropMarkers();
  };

  const handleDragStart = (event, headingId) => {
    dragRef.current = { id: headingId, drop: null };
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', headingId);
    const row = event.currentTarget.closest('.guides-outline-item');
    if (row) {
      const rect = row.getBoundingClientRect();
      event.dataTransfer.setDragImage(row, Math.min(20, rect.width / 2), rect.height / 2);
      row.classList.add('is-dragging');
    }
    event.stopPropagation();
  };

  const handleDragEnd = () => {
    dragRef.current = null;
    clearDragClasses();
  };

  const handleDragOver = (event, hoverId) => {
    const sourceId = dragRef.current?.id;
    if (!sourceId || !onMoveSection) return;
    const drop = resolveOutlineDrop(headings, sourceId, hoverId);
    if (!drop) {
      dragRef.current.drop = null;
      clearDropMarkers();
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    dragRef.current.drop = drop;
    clearDropMarkers();
    const marker = listRef.current?.querySelector(`[data-outline-id="${cssEscape(drop.markerId)}"]`);
    marker?.classList.add(drop.place === 'after' ? 'is-drop-after' : 'is-drop-before');
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const sourceId = dragRef.current?.id;
    const drop = dragRef.current?.drop;
    dragRef.current = null;
    clearDragClasses();
    if (!sourceId || !drop?.targetId) return;
    onMoveSection(sourceId, drop.targetId);
  };

  useEffect(() => {
    if (adding && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [adding]);

  useEffect(() => {
    if (!activeHeadingId || !listRef.current) return;
    const item = listRef.current.querySelector(`[data-outline-id="${cssEscape(activeHeadingId)}"]`);
    const rail = item?.closest('.guides-topic-editor-rail, .offcanvas-body');
    if (!item || !rail) return;
    const itemRect = item.getBoundingClientRect();
    const railRect = rail.getBoundingClientRect();
    if (itemRect.top < railRect.top + 8 || itemRect.bottom > railRect.bottom - 8) {
      rail.scrollTop += itemRect.top - railRect.top - rail.clientHeight / 3;
    }
  }, [activeHeadingId]);

  const startAdd = (outlineLevel, afterHeadingId = null) => {
    setDraftName('');
    setAdding({ outlineLevel, afterHeadingId });
  };

  const cancelAdd = () => {
    setAdding(null);
    setDraftName('');
  };

  const confirmAdd = () => {
    const name = draftName.trim();
    if (!name || !adding) return;
    if (adding.outlineLevel === 1) onAddTopic?.(name, adding.afterHeadingId);
    else if (adding.outlineLevel === 3) onAddLowerSubtopic?.(name, adding.afterHeadingId);
    else onAddSubtopic?.(name, adding.afterHeadingId);
    cancelAdd();
  };

  const lastTopic = [...headings].reverse().find((h) => h.outlineLevel === 1) || headings[headings.length - 1];
  const canAddSubtopic = canInsertOutlineLevel(2, topicHeadingLevel);
  const canAddLower = canInsertOutlineLevel(3, topicHeadingLevel);
  const titleLabel = titleEmpty
    ? (titleFallback || GUIDE_TITLE_PLACEHOLDER)
    : (titleFallback || GUIDE_TITLE_PLACEHOLDER);
  const ancestorIds = outlineAncestorIds(headings, activeHeadingId);

  const addPlaceholder =
    adding?.outlineLevel === 1
      ? 'Topic name'
      : adding?.outlineLevel === 3
        ? 'Third-level topic name'
        : 'Subtopic name';

  const scrollTo = (headingId) => {
    setOutlineOpen(false);
    onScrollTo?.(headingId);
  };

  const mobileToggle = (
    <Button
      size="sm"
      variant="light-solid"
      className="guides-icon-btn mt-1"
      onClick={() => setOutlineOpen(true)}
      aria-label="Open outline"
      title="Outline"
    >
      <i className="bi bi-list-ul" aria-hidden />
    </Button>
  );

  return (
    <>
      {mobileToggleHost ? createPortal(mobileToggle, mobileToggleHost) : null}
      <Offcanvas
        show={outlineOpen}
        onHide={() => setOutlineOpen(false)}
        placement="start"
        responsive="md"
        scroll
        className="guides-outline-offcanvas"
        aria-labelledby="guides-outline-offcanvas-title"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title id="guides-outline-offcanvas-title" className="guides-outline-kicker">
            In this article
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body onClick={(event) => {
          if (event.target.closest('a, .guides-read-section-btn, .guides-read-topic-btn')) {
            setOutlineOpen(false);
          }
        }}>
          <div className="p-1 py-3">
            <div className="guides-outline-kicker d-none d-md-block">In this article</div>
            <ul ref={listRef} className="guides-toc guides-outline-toc">
              {!readOnly && (
                <li className={`guides-outline-doc-title${titleEmpty ? ' guides-outline-title-missing' : ''}`}>
                  <button
                    type="button"
                    className="guides-outline-title-locked"
                    onClick={() => scrollTo(GUIDE_TITLE_PLACEHOLDER_ID)}
                    title="Document title"
                  >
                    {titleLabel}
                  </button>
                </li>
              )}

              {headings.map((h, index) => {
                const outline = h.outlineLevel || 1;
                const canDrag = Boolean(onMoveSection) && hasOutlineSibling(headings, index);
                const isCurrent = h.id === activeHeadingId;
                const isAncestor = ancestorIds.has(h.id);
                return (
                  <li
                    key={h.id}
                    data-outline-id={h.id}
                    className={`level-${Math.min(outline, 6)} guides-outline-item ${outlineRoleClass(outline)}${isCurrent ? ' is-current' : ''}${isAncestor ? ' is-ancestor' : ''}`}
                    onDragOver={(event) => handleDragOver(event, h.id)}
                    onDrop={handleDrop}
                  >
                    <div className="d-flex flex-row align-items-center sb-topic-text-hd">
                      {canDrag && (
                        <span
                          className="guides-outline-drag-handle"
                          draggable
                          title="Drag to reorder"
                          aria-label={`Reorder ${h.text}`}
                          onDragStart={(event) => handleDragStart(event, h.id)}
                          onDragEnd={handleDragEnd}
                        >
                          <i className="bi bi-grip-vertical" aria-hidden />
                        </span>
                      )}
                      <button
                        type="button"
                        aria-current={isCurrent ? 'location' : undefined}
                        onClick={() => scrollTo(h.id)}
                      >
                        {h.text}
                      </button>
                      {!readOnly && (
                      <DropdownButton
                        id={`outline-menu-${h.id}`}
                        align="end"
                        drop="down"
                        flip={false}
                        variant="light-lite"
                        size="sm"
                        className="guides-outline-menu no-btn-arrow"
                        title={(
                          <>
                            <i className="bi bi-three-dots-vertical" aria-hidden />
                            <span className="visually-hidden">Section options</span>
                          </>
                        )}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {outline === 1 && canAddSubtopic && (
                          <Dropdown.Item onClick={() => startAdd(2, h.id)}>
                            <i className="bi bi-plus me-2" aria-hidden />
                            Add subtopic
                          </Dropdown.Item>
                        )}
                        {outline === 2 && canAddLower && (
                          <Dropdown.Item onClick={() => startAdd(3, h.id)}>
                            <i className="bi bi-plus me-2" aria-hidden />
                            Add third-level topic
                          </Dropdown.Item>
                        )}
                        <Dropdown.Item
                          className="text-danger"
                          onClick={() => onRemoveSection?.(h.id)}
                        >
                          <i className="bi bi-trash me-2" aria-hidden />
                          Remove section
                        </Dropdown.Item>
                      </DropdownButton>
                      )}
                    </div>
                  </li>
                );
              })}

              {headings.length === 0 && !adding && (
                <li className="text-muted small px-2 pt-1">
                  {readOnly ? 'No headings in this article' : 'No topics yet'}
                </li>
              )}
            </ul>

            {!readOnly && (adding ? (
              <div className="guides-outline-add-form mt-2">
                <div className="guides-outline-add-row">
                  <Form.Control
                    ref={inputRef}
                    size="sm"
                    placeholder={addPlaceholder}
                    value={draftName}
                    onChange={(e) => setDraftName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                        if (draftName.trim()) confirmAdd();
                      }
                      if (e.key === 'Escape') {
                        e.preventDefault();
                        cancelAdd();
                      }
                    }}
                  />
                  <Button
                    size="sm"
                    variant="light-solid"
                    onClick={cancelAdd}
                    title="Cancel"
                    aria-label="Cancel"
                  >
                    <i className="bi bi-x" aria-hidden />
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                size="sm"
                variant="light-lite"
                className="guides-outline-add mt-2 w-100"
                onClick={() => startAdd(1, lastTopic?.id || null)}
              >
                <i className="bi bi-plus-lg me-1" aria-hidden />
                Add topic
              </Button>
            ))}
          </div>
          {children}
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
}
